import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Loader2, Play, StopCircle, Clock, CheckCircle2, AlertTriangle, Eye, Pencil, FileSpreadsheet, FileText } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const STATUS_CLS = {
  IN_PROGRESS: "border-[#93C5FD] bg-[#DBEAFE] text-[#1E3A8A]",
  SELESAI: "border-[#86EFAC] bg-[#DCFCE7] text-[#065F46]",
  PERLU_VERIFIKASI: "border-[#FCA5A5] bg-[#FEE2E2] text-[#991B1B]",
  CANCELLED: "border-[#D1D5DB] bg-[#F3F4F6] text-[#4B5563]",
};
const STATUS_LABEL = {
  IN_PROGRESS: "Berlangsung",
  SELESAI: "Selesai",
  PERLU_VERIFIKASI: "Perlu Verifikasi",
  CANCELLED: "Dibatalkan",
};
const SOURCE_LABEL = {
  MANUAL: "Manual",
  AUTO_MAX_06: "Otomatis · Batas 06:00",
  LAST_ATTENDANCE_GPS: "Otomatis · Last GPS",
  LAST_TICKET_ACTIVITY: "Otomatis · Last Ticket",
  ADMIN_CORRECTION: "Koreksi Admin",
};

const fmtWibTime = (iso) => iso ? new Date(iso).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit", hour12: false, timeZone: "Asia/Jakarta" }) : "—";
const fmtWibDT = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleDateString("en-CA", { timeZone: "Asia/Jakarta" }) + " " + d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit", hour12: false, timeZone: "Asia/Jakarta" });
};
const fmtDur = (m) => m == null ? "—" : `${Math.floor(m / 60)} jam ${m % 60} menit`;

const today = () => new Date().toLocaleDateString("en-CA", { timeZone: "Asia/Jakarta" });
const firstOfMonth = () => today().slice(0, 8) + "01";
const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
// Filter awal dari URL (?start_date&end_date&status) — dibaca sekali saat halaman dibuka.
const initialFilters = (sp) => {
  const sd = sp.get("start_date");
  const ed = sp.get("end_date");
  const st = sp.get("status");
  return {
    start_date: sd && DATE_RE.test(sd) ? sd : firstOfMonth(),
    end_date: ed && DATE_RE.test(ed) ? ed : today(),
    status: st && STATUS_LABEL[st] ? st : "",
    user_id: "",
    location_id: "",
  };
};
const inputCls = "block w-full h-9 rounded-md border border-[#E5E7EB] bg-white px-3 text-sm focus:border-[#0055FF] focus:outline-none focus:ring-2 focus:ring-[#0055FF]/20";

export default function Overtime() {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [active, setActive] = useState(null);
  const [loadingActive, setLoadingActive] = useState(true);
  const [starting, setStarting] = useState(false);
  const [stopping, setStopping] = useState(false);
  // Rekap list state
  const [rows, setRows] = useState([]);
  const [totalDur, setTotalDur] = useState(0);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState(() => initialFilters(searchParams));
  // Applied filters — snapshot dari `filters` saat load terakhir berhasil.
  // Export XLSX/PDF selalu memakai `applied` sehingga file yang diunduh
  // memuat DATASET IDENTIK dengan tabel yang sedang dilihat user.
  const [applied, setApplied] = useState(() => initialFilters(searchParams));
  const [exporting, setExporting] = useState(false);
  const [exportingPdf, setExportingPdf] = useState(false);
  const [users, setUsers] = useState([]);
  const [locations, setLocations] = useState([]);
  const [detail, setDetail] = useState(null);
  // Fase 4.2 — Koreksi (admin only)
  const [correction, setCorrection] = useState(null); // { rec, form: {ci_date, ci_time, co_date, co_time, status, reason} }
  const [savingCorr, setSavingCorr] = useState(false);
  const canCorrect = user?.role === "admin";

  const loadActive = async () => {
    setLoadingActive(true);
    try {
      const { data } = await api.get("/overtime/me/active");
      setActive(data);
    } catch { setActive(null); }
    setLoadingActive(false);
  };
  const loadList = async () => {
    setLoading(true);
    try {
      const q = new URLSearchParams();
      if (filters.start_date) q.set("start_date", filters.start_date);
      if (filters.end_date) q.set("end_date", filters.end_date);
      if (filters.status) q.set("status", filters.status);
      if (filters.user_id) q.set("user_id", filters.user_id);
      if (filters.location_id) q.set("location_id", filters.location_id);
      const { data } = await api.get(`/overtime?${q.toString()}`);
      setRows(data.items || []);
      setTotalDur(data.total_duration_minutes || 0);
      setTotal(data.total || 0);
      setApplied({ ...filters });
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
    setLoading(false);
  };
  // Fase 4.4 — Export XLSX / PDF. Reuse applied filters (snapshot yang
  // menghasilkan tabel saat ini) supaya file downloaded 1:1 dengan yang
  // dilihat user. Scope backend (_apply_koord_overtime_scope) tetap
  // sumber kebenaran security: admin=semua, koord=cohort allowed_location_ids,
  // teknisi=self. Blob download mengikuti pola AttendanceReport.
  const buildExportParams = () => {
    const p = { ...applied };
    Object.keys(p).forEach((k) => { if (!p[k]) delete p[k]; });
    return p;
  };
  const exportExcel = async () => {
    if (exporting || exportingPdf) return;
    if ((total || 0) === 0) {
      toast.error("Tidak ada data lembur untuk diekspor pada filter ini.");
      return;
    }
    setExporting(true);
    try {
      const resp = await api.get("/overtime/export", { params: buildExportParams(), responseType: "blob" });
      const blob = new Blob([resp.data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const sd = applied.start_date || "all";
      const ed = applied.end_date || "all";
      a.download = `Rekap_Lembur_${sd}_${ed}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => window.URL.revokeObjectURL(url), 1500);
      toast.success("Excel berhasil diunduh");
    } catch (e) {
      toast.error("Gagal mengekspor rekap lembur. Silakan coba lagi.");
    } finally {
      setExporting(false);
    }
  };
  const exportPdf = async () => {
    if (exporting || exportingPdf) return;
    if ((total || 0) === 0) {
      toast.error("Tidak ada data lembur untuk diekspor pada filter ini.");
      return;
    }
    setExportingPdf(true);
    try {
      const resp = await api.get("/overtime/export.pdf", { params: buildExportParams(), responseType: "blob" });
      const blob = new Blob([resp.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const sd = applied.start_date || "all";
      const ed = applied.end_date || "all";
      a.download = `Rekap_Lembur_${sd}_${ed}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => window.URL.revokeObjectURL(url), 1500);
      toast.success("PDF berhasil diunduh");
    } catch (e) {
      toast.error("Gagal mengekspor PDF. Silakan coba lagi.");
    } finally {
      setExportingPdf(false);
    }
  };
  useEffect(() => {
    // Fetch dropdown options: user picker via attendance-scope endpoint
    // (admin all / koord cohort by allowed_location_ids / teknisi 403).
    // Location picker: fetched raw, then client-side narrowed for koord
    // to only their allowed_location_ids so admin sees everything and
    // koord doesn't have an option to try an outsider location.
    Promise.all([
      api.get("/attendance/scope-users").catch(() => ({ data: [] })),
      api.get("/attendance/locations").catch(() => ({ data: [] })),
    ]).then(([u, l]) => {
      setUsers(u.data || []);
      const locs = l.data || [];
      if (user?.role === "koordinator") {
        const my = new Set(user.allowed_location_ids || []);
        setLocations(locs.filter((x) => my.has(x.id)));
      } else {
        setLocations(locs);
      }
    });
  }, [user]);
  const resetFilters = () => {
    setFilters({ start_date: firstOfMonth(), end_date: today(), status: "", user_id: "", location_id: "" });
  };
  useEffect(() => { loadActive(); loadList(); }, []);

  // Fase 4.2 — Deep-link handler: `/overtime?id=<uuid>` (from
  // notification `overtime_perlu_verifikasi`) auto-opens the Detail
  // dialog. Backend endpoint still enforces location scope, so if the
  // caller isn't allowed to see the record the fetch fails silently
  // (no data leak on the client).
  useEffect(() => {
    const dl = searchParams.get("id");
    if (!dl) return;
    (async () => {
      try {
        const { data } = await api.get(`/overtime/${dl}`);
        setDetail(data);
      } catch {
        toast.error("Record lembur tidak dapat dibuka atau di luar cakupan Anda");
      }
      // Clear the query param so navigating within the page doesn't re-trigger.
      const next = new URLSearchParams(searchParams);
      next.delete("id");
      setSearchParams(next, { replace: true });
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Split an ISO WIB timestamp into `{date: YYYY-MM-DD, time: HH:mm}` for form pre-fill.
  const isoToWibParts = (iso) => {
    if (!iso) return { date: "", time: "" };
    const d = new Date(iso);
    return {
      date: d.toLocaleDateString("en-CA", { timeZone: "Asia/Jakarta" }),
      time: d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit", hour12: false, timeZone: "Asia/Jakarta" }),
    };
  };
  const wibPartsToIso = (dateStr, timeStr) => {
    if (!dateStr || !timeStr) return null;
    // WIB is UTC+7 → construct WIB local wallclock then shift to UTC.
    const [Y, M, D] = dateStr.split("-").map(Number);
    const [h, m] = timeStr.split(":").map(Number);
    const utcMs = Date.UTC(Y, M - 1, D, h - 7, m);
    return new Date(utcMs).toISOString();
  };

  const openKoreksi = (r) => {
    const ci = isoToWibParts(r.start_at);
    const co = isoToWibParts(r.end_at);
    setCorrection({
      rec: r,
      form: {
        ci_date: ci.date, ci_time: ci.time,
        co_date: co.date, co_time: co.time,
        status: r.status,
        reason: "",
      },
    });
  };
  const submitKoreksi = async () => {
    if (!correction) return;
    const { rec, form } = correction;
    if (!form.reason || form.reason.trim().length < 5) {
      toast.error("Alasan minimal 5 karakter"); return;
    }
    // Fase 4.7 — regex HH:mm 24-jam sama seperti Fase 4.6 (Koreksi Absensi
    // reguler). Menolak sebelum wibPartsToIso agar submit gagal cepat.
    const HHMM_RE = /^([01]\d|2[0-3]):[0-5]\d$/;
    if (form.ci_time && !HHMM_RE.test(form.ci_time)) {
      toast.error("Format Jam Mulai tidak valid. Gunakan HH:mm (00-23:00-59)"); return;
    }
    if (form.co_time && !HHMM_RE.test(form.co_time)) {
      toast.error("Format Jam Selesai tidak valid. Gunakan HH:mm (00-23:00-59)"); return;
    }
    const body = {};
    const newStart = wibPartsToIso(form.ci_date, form.ci_time);
    const newEnd = form.co_date && form.co_time ? wibPartsToIso(form.co_date, form.co_time) : null;
    if (newStart !== rec.start_at) body.start_at = newStart;
    if ((newEnd || null) !== (rec.end_at || null)) body.end_at = newEnd;
    if (form.status !== rec.status) body.status = form.status;
    if (Object.keys(body).length === 0) { toast.error("Tidak ada perubahan"); return; }
    body.reason = form.reason.trim();
    setSavingCorr(true);
    try {
      const { data } = await api.patch(`/overtime/${rec.id}`, body);
      toast.success("Koreksi tersimpan");
      setDetail(data);
      setCorrection(null);
      await loadList();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Gagal menyimpan koreksi");
    }
    setSavingCorr(false);
  };

  const mulai = async () => {
    setStarting(true);
    try {
      await api.post("/overtime/check-in");
      toast.success("Sesi lembur dimulai");
      await loadActive(); await loadList();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Gagal memulai lembur");
    }
    setStarting(false);
  };
  const selesai = async () => {
    setStopping(true);
    try {
      await api.post("/overtime/check-out");
      toast.success("Sesi lembur diselesaikan");
      await loadActive(); await loadList();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Gagal menyelesaikan lembur");
    }
    setStopping(false);
  };

  // Fase 5.1 — koordinator kini dapat self-overtime (sama seperti teknisi).
  // Backend POST /overtime/check-in dan check-out sudah role-agnostic
  // (pakai `user["id"]` dari session, tidak filter role). Root cause bug
  // sebelumnya murni frontend: card self-overtime hanya render untuk role
  // teknisi. Gate diperluas ke koord tanpa menyentuh filter/export/Koreksi
  // yang tetap admin/koord-only atau admin-only sesuai spec.
  const isSelfOvertimeRole = user?.role === "teknisi" || user?.role === "koordinator";

  return (
    <div className="max-w-6xl mx-auto space-y-6 px-2 md:px-0" data-testid="overtime-page">
      <div>
        <div className="text-[11px] font-bold uppercase tracking-[0.25em] text-[#9CA3AF]">Operasional</div>
        <h1 className="font-display text-3xl sm:text-4xl font-black tracking-tighter mt-1">Absensi Lembur</h1>
        <p className="text-sm text-[#6B7280] mt-1">Jam lembur: mulai 18:01 WIB sampai maksimal 06:00 WIB hari berikutnya.</p>
      </div>

      {/* Self overtime card — teknisi & koordinator can start their own
          session; admin does not see this widget by design. */}
      {isSelfOvertimeRole && (
        <div className="rounded-lg border border-[#E5E7EB] bg-white p-4 md:p-5 space-y-3" data-testid="overtime-self-card">
          {loadingActive ? (
            <div className="flex items-center gap-2 text-sm text-[#6B7280]"><Loader2 className="h-4 w-4 animate-spin" /> Memuat status…</div>
          ) : active ? (
            <>
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <div className="flex items-center gap-2">
                  <span className={`inline-flex h-6 px-2 items-center rounded-full border text-[10px] font-bold ${STATUS_CLS.IN_PROGRESS}`}>Berlangsung</span>
                  <span className="text-xs text-[#6B7280]">Sesi lembur aktif</span>
                </div>
                <div className="text-xs text-[#9CA3AF] font-mono-tab" data-testid="overtime-start-at">Mulai: {fmtWibDT(active.start_at)} WIB</div>
              </div>
              <button
                onClick={selesai}
                disabled={stopping}
                data-testid="btn-overtime-stop"
                className="w-full sm:w-auto h-11 sm:h-10 px-5 rounded-md bg-[#991B1B] hover:bg-[#7F1D1D] text-white text-sm font-bold flex items-center justify-center gap-1.5 disabled:opacity-50"
              >
                {stopping ? <Loader2 className="h-4 w-4 animate-spin" /> : <StopCircle className="h-4 w-4" />}
                Selesai Lembur
              </button>
            </>
          ) : (
            <>
              <div className="text-sm text-[#6B7280]">Belum ada sesi lembur aktif. Klik tombol di bawah untuk mulai.</div>
              <button
                onClick={mulai}
                disabled={starting}
                data-testid="btn-overtime-start"
                className="w-full sm:w-auto h-11 sm:h-10 px-5 rounded-md bg-[#065F46] hover:bg-[#064E3B] text-white text-sm font-bold flex items-center justify-center gap-1.5 disabled:opacity-50"
              >
                {starting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />}
                Mulai Lembur
              </button>
            </>
          )}
        </div>
      )}

      {/* Filters (admin/koord/tekmisi own history) */}
      <div className="rounded-lg border border-[#E5E7EB] bg-white p-3 md:p-4 flex flex-col sm:flex-row sm:items-end sm:flex-wrap gap-2">
        <div className="flex-1 min-w-[9rem]">
          <label className="block text-[10px] font-bold uppercase tracking-widest text-[#4B5563] mb-1">Tanggal Mulai</label>
          <input type="date" value={filters.start_date} onChange={(e) => setFilters({ ...filters, start_date: e.target.value })} className={inputCls} data-testid="ov-filter-start" />
        </div>
        <div className="flex-1 min-w-[9rem]">
          <label className="block text-[10px] font-bold uppercase tracking-widest text-[#4B5563] mb-1">Tanggal Selesai</label>
          <input type="date" value={filters.end_date} onChange={(e) => setFilters({ ...filters, end_date: e.target.value })} className={inputCls} data-testid="ov-filter-end" />
        </div>
        {user?.role !== "teknisi" && (
          <div className="flex-1 min-w-[10rem]">
            <label className="block text-[10px] font-bold uppercase tracking-widest text-[#4B5563] mb-1">User</label>
            <select value={filters.user_id} onChange={(e) => setFilters({ ...filters, user_id: e.target.value })} className={inputCls} data-testid="ov-filter-user">
              <option value="">Semua</option>
              {users.map((u) => (
                <option key={u.id} value={u.id}>{u.name} ({u.username})</option>
              ))}
            </select>
          </div>
        )}
        <div className="flex-1 min-w-[9rem]">
          <label className="block text-[10px] font-bold uppercase tracking-widest text-[#4B5563] mb-1">Lokasi</label>
          <select value={filters.location_id} onChange={(e) => setFilters({ ...filters, location_id: e.target.value })} className={inputCls} data-testid="ov-filter-location">
            <option value="">Semua</option>
            {locations.map((l) => (
              <option key={l.id} value={l.id}>{l.name}</option>
            ))}
          </select>
        </div>
        <div className="flex-1 min-w-[9rem]">
          <label className="block text-[10px] font-bold uppercase tracking-widest text-[#4B5563] mb-1">Status</label>
          <select value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value })} className={inputCls} data-testid="ov-filter-status">
            <option value="">Semua</option>
            <option value="IN_PROGRESS">Berlangsung</option>
            <option value="SELESAI">Selesai</option>
            <option value="PERLU_VERIFIKASI">Perlu Verifikasi</option>
            <option value="CANCELLED">Dibatalkan</option>
          </select>
        </div>
        <div className="flex gap-2 sm:self-end">
          <button onClick={resetFilters} data-testid="ov-btn-reset" className="h-10 sm:h-9 px-3 rounded-md border border-[#E5E7EB] hover:bg-[#F8F9FA] text-sm font-semibold">Reset</button>
          <button onClick={loadList} data-testid="ov-btn-apply" className="h-10 sm:h-9 px-4 rounded-md bg-[#0055FF] hover:bg-[#0044CC] text-white text-sm font-bold">Terapkan</button>
        </div>
      </div>

      {/* Summary */}
      <div className="rounded-lg border border-[#E5E7EB] bg-[#F0F4FF] p-3 flex items-center justify-between flex-wrap gap-2" data-testid="overtime-summary">
        <div className="text-xs">
          <div className="text-[10px] font-bold uppercase tracking-widest text-[#4B5563]">Total Durasi Lembur</div>
          <div className="text-lg font-black font-mono-tab" data-testid="overtime-total-duration">{fmtDur(totalDur)}</div>
        </div>
        <div className="text-xs text-right">
          <div className="text-[10px] font-bold uppercase tracking-widest text-[#4B5563]">Jumlah Record</div>
          <div className="text-lg font-black font-mono-tab" data-testid="overtime-total-count">{total}</div>
        </div>
      </div>

      {/* Fase 4.4 — Export XLSX / PDF (Admin + Koordinator only). Teknisi
          tetap bisa melihat rekap sendiri tapi tombol export disembunyikan
          — backend juga tidak me-gate teknisi, jadi guard di sini murni UX. */}
      {user?.role !== "teknisi" && (
        <div className="flex flex-wrap gap-2 justify-end" data-testid="overtime-export-bar">
          <button
            onClick={exportExcel}
            disabled={exporting || exportingPdf || loading || total === 0}
            data-testid="btn-overtime-export-excel"
            className="h-9 px-3 rounded-md bg-[#065F46] hover:bg-[#064E3B] text-white text-sm font-bold flex items-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {exporting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <FileSpreadsheet className="h-3.5 w-3.5" />}
            {exporting ? "Menyiapkan Excel…" : "Export Excel"}
          </button>
          <button
            onClick={exportPdf}
            disabled={exporting || exportingPdf || loading || total === 0}
            data-testid="btn-overtime-export-pdf"
            className="h-9 px-3 rounded-md bg-[#991B1B] hover:bg-[#7F1D1D] text-white text-sm font-bold flex items-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {exportingPdf ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <FileText className="h-3.5 w-3.5" />}
            {exportingPdf ? "Menyiapkan PDF…" : "Export PDF"}
          </button>
        </div>
      )}

      {/* Desktop table */}
      <div className="hidden md:block rounded-lg border border-[#E5E7EB] bg-white overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-sm text-[#6B7280]"><Loader2 className="h-4 w-4 animate-spin inline" /> Memuat…</div>
        ) : rows.length === 0 ? (
          <div className="p-8 text-center text-sm text-[#9CA3AF]">Belum ada data lembur.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#F8F9FA] text-[10px] font-bold uppercase tracking-widest text-[#6B7280]">
                <tr>
                  <th className="px-4 py-2 text-left">Tanggal</th>
                  <th className="px-4 py-2 text-left">User</th>
                  <th className="px-4 py-2 text-left">Mulai</th>
                  <th className="px-4 py-2 text-left">Selesai</th>
                  <th className="px-4 py-2 text-left">Durasi</th>
                  <th className="px-4 py-2 text-left">Status</th>
                  <th className="px-4 py-2 text-left">Source Selesai</th>
                  <th className="px-4 py-2 text-right">Aksi</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#F3F4F6]">
                {rows.map((r) => (
                  <tr key={r.id} data-testid={`ov-row-${r.id}`}>
                    <td className="px-4 py-2 font-mono-tab text-xs">{r.overtime_date}</td>
                    <td className="px-4 py-2 text-xs">{r.user_name} <span className="text-[#9CA3AF]">({r.username})</span></td>
                    <td className="px-4 py-2 font-mono-tab text-xs">{fmtWibTime(r.start_at)}</td>
                    <td className="px-4 py-2 font-mono-tab text-xs">{fmtWibTime(r.end_at)}</td>
                    <td className="px-4 py-2 font-mono-tab text-xs">{fmtDur(r.duration_minutes)}</td>
                    <td className="px-4 py-2"><span className={`inline-flex h-5 px-2 items-center rounded-full border text-[10px] font-bold ${STATUS_CLS[r.status] || ""}`}>{STATUS_LABEL[r.status] || r.status}</span></td>
                    <td className="px-4 py-2 text-xs">{r.end_source ? (SOURCE_LABEL[r.end_source] || r.end_source) : "—"}</td>
                    <td className="px-4 py-2 text-right">
                      <button onClick={() => setDetail(r)} className="inline-flex items-center gap-1 h-7 px-2 rounded-md border border-[#E5E7EB] text-xs text-[#0055FF]" data-testid={`ov-detail-${r.id}`}><Eye className="h-3 w-3" />Detail</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Mobile cards */}
      <ul className="md:hidden space-y-3" data-testid="mobile-overtime-list">
        {loading && <li className="rounded-lg border border-[#E5E7EB] bg-white p-6 text-center text-sm text-[#6B7280]"><Loader2 className="h-4 w-4 animate-spin inline" /> Memuat…</li>}
        {!loading && rows.length === 0 && <li className="rounded-lg border border-[#E5E7EB] bg-white p-6 text-center text-sm text-[#9CA3AF]">Belum ada data lembur.</li>}
        {!loading && rows.map((r) => (
          <li key={r.id} className="rounded-lg border border-[#E5E7EB] bg-white p-4 space-y-3" data-testid={`ov-card-${r.id}`}>
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <div className="text-sm font-bold break-words">{r.user_name}</div>
                <div className="text-[10px] uppercase tracking-widest text-[#9CA3AF]">{r.username} · {r.user_role}</div>
              </div>
              <span className={`inline-flex h-6 px-2 items-center rounded-full border text-[10px] font-bold whitespace-nowrap ${STATUS_CLS[r.status] || ""}`}>{STATUS_LABEL[r.status] || r.status}</span>
            </div>
            <div className="border-t border-[#E5E7EB] pt-3 space-y-2 text-xs">
              <div><div className="text-[10px] font-bold uppercase tracking-widest text-[#9CA3AF]">Tanggal</div><div className="font-mono-tab">{r.overtime_date}</div></div>
              <div className="grid grid-cols-2 gap-3">
                <div><div className="text-[10px] font-bold uppercase tracking-widest text-[#9CA3AF]">Mulai</div><div className="font-mono-tab">{fmtWibTime(r.start_at)}</div></div>
                <div><div className="text-[10px] font-bold uppercase tracking-widest text-[#9CA3AF]">Selesai</div><div className="font-mono-tab">{fmtWibTime(r.end_at)}</div></div>
              </div>
              <div><div className="text-[10px] font-bold uppercase tracking-widest text-[#9CA3AF]">Durasi</div><div className="font-mono-tab font-bold">{fmtDur(r.duration_minutes)}</div></div>
              <div><div className="text-[10px] font-bold uppercase tracking-widest text-[#9CA3AF]">Source Selesai</div><div className="break-words">{r.end_source ? (SOURCE_LABEL[r.end_source] || r.end_source) : "—"}</div></div>
            </div>
            <button onClick={() => setDetail(r)} data-testid={`m-ov-detail-${r.id}`} className="h-10 w-full rounded-md border border-[#E5E7EB] hover:bg-[#F8F9FA] text-sm font-semibold text-[#0055FF] flex items-center justify-center gap-1.5"><Eye className="h-4 w-4" />Detail</button>
          </li>
        ))}
      </ul>

      {/* Detail */}
      <Dialog open={!!detail} onOpenChange={(v) => !v && setDetail(null)}>
        <DialogContent data-testid="overtime-detail" className="max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display text-xl font-bold tracking-tight">Detail Lembur</DialogTitle></DialogHeader>
          {detail && (
            <div className="space-y-3 text-sm">
              <Row k="User" v={`${detail.user_name} (${detail.username})`} />
              <Row k="Tanggal" v={detail.overtime_date} />
              <Row k="Mulai" v={`${fmtWibDT(detail.start_at)} WIB`} />
              <Row k="Selesai" v={detail.end_at ? `${fmtWibDT(detail.end_at)} WIB` : "—"} />
              <Row k="Durasi" v={fmtDur(detail.duration_minutes)} />
              <Row k="Status" v={STATUS_LABEL[detail.status] || detail.status} />
              <Row k="Source Mulai" v={SOURCE_LABEL[detail.start_source] || detail.start_source} />
              <Row k="Source Selesai" v={detail.end_source ? (SOURCE_LABEL[detail.end_source] || detail.end_source) : "—"} />
              {(detail.corrections?.length ?? 0) > 0 && (
                <div>
                  <div className="text-[10px] font-bold uppercase tracking-widest text-[#4B5563] mt-2">Riwayat Koreksi ({detail.corrections.length})</div>
                  {detail.corrections.map((c, i) => (
                    <div key={i} className="mt-1 p-2 border border-[#E5E7EB] rounded text-xs">
                      <div>Oleh: <b>{c.actor_username}</b> · {fmtWibDT(c.timestamp)}</div>
                      <div>Alasan: {c.reason}</div>
                    </div>
                  ))}
                </div>
              )}
              {canCorrect && (
                <button
                  onClick={() => openKoreksi(detail)}
                  data-testid="btn-open-koreksi-lembur"
                  className="w-full sm:w-auto h-11 sm:h-10 px-5 rounded-md bg-[#0055FF] hover:bg-[#0044CC] text-white text-sm font-bold flex items-center justify-center gap-1.5 mt-3"
                >
                  <Pencil className="h-4 w-4" /> Koreksi Lembur
                </button>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Fase 4.2 — Koreksi Lembur (admin only). Reuses PATCH /overtime/{id}. */}
      <Dialog open={!!correction} onOpenChange={(v) => !v && setCorrection(null)}>
        <DialogContent data-testid="overtime-correction-dialog" className="max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display text-xl font-bold tracking-tight">Koreksi Lembur</DialogTitle>
          </DialogHeader>
          {correction && (() => {
            // Fase 4.7 — masked HH:mm 24-jam handler + inline validation.
            // Native `<input type="time">` di Chromium/Android mengikuti OS
            // locale sehingga user dengan OS en-US melihat AM/PM. Pattern
            // masked text ini bypass native picker (sama dg Fase 4.6).
            const HHMM_RE = /^([01]\d|2[0-3]):[0-5]\d$/;
            const ciTimeInvalid = !!correction.form.ci_time && !HHMM_RE.test(correction.form.ci_time);
            const coTimeInvalid = !!correction.form.co_time && !HHMM_RE.test(correction.form.co_time);
            const setTime = (field, raw) => {
              const prev = correction.form[field] || "";
              let v = raw.replace(/[^0-9:]/g, "").replace(/:+/g, ":");
              if (v.length === 2 && !v.includes(":") && prev.length < v.length) v += ":";
              if (v.length > 5) v = v.slice(0, 5);
              setCorrection({ ...correction, form: { ...correction.form, [field]: v } });
            };
            const timeInputCls = (invalid) =>
              `${inputCls} font-mono tabular-nums text-center ${invalid ? "border-[#DC2626]" : ""}`;
            return (
            <div className="space-y-3 text-sm">
              <div className="text-xs text-[#6B7280]">Record: <b>{correction.rec.user_name}</b> · {correction.rec.overtime_date}</div>
              <div>
                <label className="block text-[10px] font-bold uppercase tracking-widest text-[#4B5563] mb-1.5">Mulai (WIB)</label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  <input type="date" value={correction.form.ci_date} onChange={(e) => setCorrection({ ...correction, form: { ...correction.form, ci_date: e.target.value } })} className={inputCls} data-testid="ov-corr-ci-date" />
                  <input
                    type="text"
                    inputMode="numeric"
                    pattern="[0-2][0-9]:[0-5][0-9]"
                    placeholder="HH:mm"
                    maxLength={5}
                    value={correction.form.ci_time}
                    onChange={(e) => setTime("ci_time", e.target.value)}
                    className={timeInputCls(ciTimeInvalid)}
                    data-testid="ov-corr-ci-time"
                    aria-label="Jam Mulai 24 jam WIB"
                  />
                </div>
                {ciTimeInvalid && <p className="text-[11px] text-[#DC2626] mt-1" data-testid="ov-corr-ci-time-error">Format jam tidak valid. Gunakan HH:mm (00-23:00-59).</p>}
              </div>
              <div>
                <label className="block text-[10px] font-bold uppercase tracking-widest text-[#4B5563] mb-1.5">Selesai (WIB) — max 06:00 hari berikutnya</label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  <input type="date" value={correction.form.co_date} onChange={(e) => setCorrection({ ...correction, form: { ...correction.form, co_date: e.target.value } })} className={inputCls} data-testid="ov-corr-co-date" />
                  <input
                    type="text"
                    inputMode="numeric"
                    pattern="[0-2][0-9]:[0-5][0-9]"
                    placeholder="HH:mm"
                    maxLength={5}
                    value={correction.form.co_time}
                    onChange={(e) => setTime("co_time", e.target.value)}
                    className={timeInputCls(coTimeInvalid)}
                    data-testid="ov-corr-co-time"
                    aria-label="Jam Selesai 24 jam WIB"
                  />
                </div>
                {coTimeInvalid && <p className="text-[11px] text-[#DC2626] mt-1" data-testid="ov-corr-co-time-error">Format jam tidak valid. Gunakan HH:mm (00-23:00-59).</p>}
                <p className="text-[10px] text-[#9CA3AF] mt-1">Kosongkan kedua field untuk membersihkan Selesai (record tetap PERLU_VERIFIKASI). Format 24 jam (00:00–23:59), tanpa AM/PM.</p>
              </div>
              <div>
                <label className="block text-[10px] font-bold uppercase tracking-widest text-[#4B5563] mb-1.5">Status</label>
                <select value={correction.form.status} onChange={(e) => setCorrection({ ...correction, form: { ...correction.form, status: e.target.value } })} className={inputCls} data-testid="ov-corr-status">
                  <option value="IN_PROGRESS">Berlangsung</option>
                  <option value="SELESAI">Selesai</option>
                  <option value="PERLU_VERIFIKASI">Perlu Verifikasi</option>
                  <option value="CANCELLED">Dibatalkan</option>
                </select>
              </div>
              <div>
                <label className="block text-[10px] font-bold uppercase tracking-widest text-[#4B5563] mb-1.5">Alasan (min 5 karakter, WAJIB)</label>
                <textarea rows={3} value={correction.form.reason} onChange={(e) => setCorrection({ ...correction, form: { ...correction.form, reason: e.target.value } })} className={`${inputCls} h-auto py-2`} placeholder="Contoh: Konfirmasi teknisi via telepon, lembur sampai 23:30" data-testid="ov-corr-reason" />
              </div>
            </div>
            );
          })()}
          <div className="pt-3 mt-2 border-t border-[#E5E7EB] grid grid-cols-2 gap-2 sm:flex sm:justify-end sm:gap-2">
            <button onClick={() => setCorrection(null)} className="h-11 sm:h-10 px-4 rounded-md border border-[#E5E7EB] text-sm font-semibold hover:bg-[#F8F9FA]" data-testid="ov-corr-cancel">Batal</button>
            <button onClick={submitKoreksi} disabled={savingCorr} className="inline-flex items-center justify-center gap-1.5 h-11 sm:h-10 px-4 rounded-md bg-[#065F46] hover:bg-[#064E3B] text-white text-sm font-bold disabled:opacity-40" data-testid="ov-corr-submit">
              {savingCorr ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
              Simpan Koreksi
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Row({ k, v }) {
  return <div className="flex justify-between items-start gap-3"><div className="text-[#6B7280]">{k}</div><div className="font-semibold text-right break-words min-w-0 max-w-[65%]">{v || "—"}</div></div>;
}
